import { rem } from 'polished'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { useInView } from 'react-intersection-observer'
import { connect } from 'react-redux'
import { useLocation, useParams } from 'react-router-dom'
import styled from 'styled-components'

import { bedbankOfferProductDetailView } from 'actions/AnalyticsActions'
import { fetchBedbankOfferById } from 'actions/BedbankOfferActions'
import { fetchTraderInformation, offerViewed } from 'actions/OfferActions'
import { accommodationOfferClicked } from 'analytics/eventDefinitions'
import { usePageActivityTracking } from 'analytics/snowplow/helpers/useActivityTracking'
import { fireEcommerceGTMEvent } from 'api/googleTagManager'
import ErrorBoundary from 'components/Common/ErrorBoundary'
import HeadData from 'components/Common/HeadData'
import HttpStatus from 'components/Common/HttpStatus'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import OfferRecommendations from 'components/Common/OfferRecommendations'
import OfferPageSignUpWall from 'components/Common/OfferPageSignUpWall'
import Pane from 'components/Common/Pane'
import RedirectWithStatus from 'components/Common/RedirectWithStatus'
import SoftOfferPageWalledGarden from 'components/Common/SoftOfferPageWalledGarden'
import BedbankOfferContainer from 'components/OfferPage/Bedbank/BedbankOfferContainer'
import BedbankNavigableOfferContent from 'components/OfferPage/NavigableOfferContent/BedbankNavigableOfferContent'
import PropertyNavigableOfferContent from 'components/OfferPage/NavigableOfferContent/PropertyNavigableOfferContent'
import PropertyContainer from 'components/OfferPage/Property/PropertyContainer'
import { BottomMobileRelatedTravelItems } from 'components/OfferPage/RelatedTravelItems'
import Error404PageWithOffers from 'components/Pages/Error404PageWithOffers/Error404PageWithOffers'
import Error500Page from 'components/Pages/Error500Page'
import config from 'constants/config'
import { OFFER_TYPE_BED_BANK } from 'constants/offer'
import { Experiments } from 'constants/experiments'
import { FORCED_SPP_BEDBANK_OFFER_IDS } from 'constants/property'
import { urlHashDatePicker } from 'constants/url'
import AnalyticsPageContext, { AnalyticsPage } from 'contexts/Analytics/analyticsPageContext'
import { LuxPlusLabelProvider } from 'contexts/LuxPlusLabelContext'
import useEuropeOfferPageWalledGarden from 'hooks/OfferPage/useEuropeOfferPagWalledGarden'
import useProperty from 'hooks/Offers/useProperty'
import usePropertyOfferMapping from 'hooks/Offers/usePropertyOfferMapping'
import useExperiment from 'hooks/Experiments/useExperiment'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { isEnabledForFeature } from 'lib/config/featureFlagUtils'
import { ENABLED_OFFER_TYPES_RECENTLY_VIEWED } from 'lib/offer/highIntentOffers'
import offerPageURL from 'lib/offer/offerPageURL'
import OfferPageAssistantRailLayout from 'tripPlanner/assistant/surfaces/offerPage/OfferPageAssistantRailLayout'

const NavigableContentPane = styled(Pane)`
  padding-bottom: ${rem(24)};
`
const OfferPageWrapper = styled.div`
  display: flex;
  flex-direction: column;
`

const ObservableWrapper = styled.div`
  padding-top: 1px;
`

interface OwnProps {
  locationHash: string
  offerId?: string
  offerSlug?: string
  propertyPageOfferId?: string
}

interface Props extends OwnProps {
  fetching: boolean
  offer?: App.BedbankOffer
  offerError?: unknown
  regionCode: string
  currency: string
  headlessMode: boolean
  traderInformation?: App.TraderInformation
  relatedTravelItems: App.OfferState['relatedTravelItems']
}

function getErrorStatus(error: unknown): number | undefined {
  return error && typeof error === 'object' && 'status' in error && typeof error.status === 'number' ?
    error.status :
    undefined
}

function BedbankOfferPage(props: Props) {
  const {
    fetching,
    locationHash,
    offerId: routeOfferId,
    offerSlug,
    offer,
    offerError,
    regionCode,
    currency,
    headlessMode,
    relatedTravelItems,
    traderInformation,
    propertyPageOfferId,
  } = props

  const intl = useIntl()
  const dispatch = useAppDispatch()
  const [showNavOfferContent, setShowNavOfferContent] = useState(false)

  const offerId = propertyPageOfferId || routeOfferId || ''
  const dataLoaded = useRef(false)

  const hasPromotions = !!offer?.promotions.length
  const isForcedSpp = FORCED_SPP_BEDBANK_OFFER_IDS.has(offerId)
  const {
    isAnyVariant: canUsePropertyPageForPromotions,
  } = useExperiment(Experiments.sppBedbankPromos, { canActivate: false })
  // Keep this condition consistent with `fetchBedbankOfferAndBreadcrumbs.ts`. Also determines which
  // assistant snapshot identity/rate pipeline a given offer registers under (bedbank vs spp_property,
  // see OfferPageAssistantSurfaceRegistry) — the same offer can register under either identity
  // depending on experiment bucketing. Degrades gracefully either way.
  const shouldUsePropertyPage = !hasPromotions || isForcedSpp || canUsePropertyPageForPromotions
  const [propertyMapping, fetchingPropertyMapping] = usePropertyOfferMapping(OFFER_TYPE_BED_BANK, offer?.id, {
    disabled: !shouldUsePropertyPage,
  })
  // TODO(HP-1614): This is a workaround for the accommodation service mapping endpoint eagerly
  // returning a mapping, even though bedbank rates are disabled, meaning we might not be guaranteed
  // to successfully fetch property content.
  const hasValidPropertyMapping = !!propertyMapping?.bedbankPropertyIds?.includes(offer?.id ?? '')
  // TODO(XTRNT-570): This is a workaround for accommodation service potentially returning property
  // content even though bedbank rates are disabled.
  const [property] = useProperty(propertyMapping?.propertyId, { disabled: !hasValidPropertyMapping })
  // NB: Redundant `propertyMapping` assertion is for type narrowing.
  const shouldUseSpp = !!propertyMapping && hasValidPropertyMapping && !!property

  const isWalledGardenEuropeOffersEnabled = useEuropeOfferPageWalledGarden()

  const isSpoofed = useAppSelector(state => state.auth.account.isSpoofed)
  const offerSharedState = useAppSelector(state => state.recommendations.offerSharedState[offerId])
  const lereOfferStateAvailable = !!offerSharedState

  const recommendationFilters = useMemo<App.OfferListFilters>(() => ({
    propertyId: `bedbank:${offerId}`,
    searchNearby: true,
    checkIn: offerSharedState?.checkIn,
    checkOut: offerSharedState?.checkOut,
    rooms: offerSharedState?.rooms,
    lere: 'graph',
    baseOfferId: offerId,
  }), [offerId, offerSharedState?.checkIn, offerSharedState?.checkOut, offerSharedState?.rooms])

  const similarOffersTitle = offer?.property.name
    ? intl.formatMessage(
        {
          id: '9VU-fbcqdK87hNFh0tLz',
          defaultMessage: 'Similar to {hotelName}',
          description: 'Title for similar offers section on the offer listing or detail page',
        },
        { hotelName: offer.property.name },
      )
    : intl.formatMessage({
        id: 'nJUaZcgPjgC99vyMblPW',
        defaultMessage: 'Similar offers',
        description: 'Fallback title for the similar offers section when the property name is unavailable',
      })

  useEffect(() => {
    if (offer) {
      // we use dataLoaded ref to prevent multiple analytics pushes
      fireEcommerceGTMEvent(accommodationOfferClicked(offer, currency))
      dataLoaded.current = true
    }
  }, [offer, currency])

  usePageActivityTracking()

  useEffect(() => {
    if (!showNavOfferContent && locationHash && locationHash !== urlHashDatePicker) {
      setShowNavOfferContent(true)
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [locationHash])

  useEffect(() => {
    if (showNavOfferContent) {
      window.location.href = locationHash
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [showNavOfferContent])

  useEffect(() => {
    if (offer && ENABLED_OFFER_TYPES_RECENTLY_VIEWED.has(offer.type)) {
      dispatch(offerViewed(offer.id, offer.type, !offer.sell?.price))
    }
  }, [offer, dispatch])

  // This one is used to load the section before the user gets to it
  const [navigableRef, navigableInView] = useInView({
    triggerOnce: true,
    rootMargin: '500px',
  })

  useEffect(() => {
    dispatch(fetchBedbankOfferById(offerId, { regionCode }))
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [offerId])

  useEffect(() => {
    if (isEnabledForFeature(config.TRADER_INFORMATION_REGIONS!, regionCode)) {
      dispatch(fetchTraderInformation(offerId, regionCode))
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [offerId])

  useEffect(() => {
    if (offer) {
      dispatch(bedbankOfferProductDetailView(offer))
      window.dataLayer?.push({ offerType: offer.type })
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [offerId])

  const [footerElement, setFooterElement] = useState<HTMLDivElement | null>(null)

  if (offerError) {
    if (getErrorStatus(offerError) === 404) {
      return <HttpStatus code={404}>
        <Error404PageWithOffers />
      </HttpStatus>
    } else {
      return <HttpStatus code={500}>
        <Error500Page error={offerError} />
      </HttpStatus>
    }
  }

  if (fetchingPropertyMapping || (hasValidPropertyMapping && !property) || fetching || !offer) {
    return <LoadingIndicator data-testid="bedbank-offer-loading-indicator" />
  }

  if (offerSlug && (offer.slug !== offerSlug)) {
    return <RedirectWithStatus code={301} to={`/${regionCode.toLowerCase()}${offerPageURL(offer)}`} />
  }

  const metaDescription = intl.formatMessage(
    {
      id: 'wPY4Cx4pSI2Xj1_11b_J',
      defaultMessage: 'Book the best deal for {offerName}, {city} with Luxury Escapes today. The world’s best escapes, handpicked by experts!',
      description: 'Meta description for bedbank hotel offer page showing offer name and city',
    },
    { offerName: offer.name, city: offer.property.address.city },
  )
  const breadCrumbItems = relatedTravelItems[offer?.id] || []
  const travelDestinationsTitle = intl.formatMessage({
    id: 'aPtZf8zHDqKbAE6A-h8t',
    defaultMessage: 'Travel Destinations',
    description: 'Title for related travel destinations section on bedbank offer page',
  })

  return <AnalyticsPageContext.Provider value={AnalyticsPage.offerPage}>
    <HeadData
      title={intl.formatMessage(
        {
          id: 'RsGI_Cs5BMyY6dyTa9Xc',
          defaultMessage: '{offerName}, {city} - {siteTitle} {regionCode}',
          description: 'Browser tab title for bedbank hotel offer page on the offer detail page',
        },
        { offerName: offer.name, city: offer.property.address.city, siteTitle: config.title, regionCode },
      )}
      description={metaDescription}
      imageId={offer.images[0]?.id || undefined}
    />
    <OfferPageWrapper className="OfferPage">
      <OfferPageAssistantRailLayout headlessMode={headlessMode}>
        <LuxPlusLabelProvider page="offer-page">
          {!shouldUseSpp && <BedbankOfferContainer
            offerId={offerId}
            offer={offer}
            footerElement={footerElement}
          />}
          {shouldUseSpp && <PropertyContainer
            property={property}
            offer={offer}
          />}
        </LuxPlusLabelProvider>
        {!shouldUseSpp && <SoftOfferPageWalledGarden
          bypassWall={isWalledGardenEuropeOffersEnabled}
          wall={<OfferPageSignUpWall offerType="hotel" />}
        />}
        <ObservableWrapper ref={navigableRef}>
          <NavigableContentPane>
            {(navigableInView || showNavOfferContent) && <>
              {!shouldUseSpp && (
                <BedbankNavigableOfferContent
                  offer={offer}
                  traderInformation={traderInformation}
                  hash={locationHash}
                />
              )}
              {shouldUseSpp && (
                <PropertyNavigableOfferContent
                  offer={offer}
                  property={property}
                  propertyOfferMapping={propertyMapping}
                  traderInformation={traderInformation}
                  hash={locationHash}
                />
              )}
            </>}
          </NavigableContentPane>
        </ObservableWrapper>
        {shouldUseSpp && lereOfferStateAvailable && !isSpoofed && (
          <ErrorBoundary fallback={null}>
            <OfferRecommendations
              title={similarOffersTitle}
              offerId={offerId}
              filters={recommendationFilters}
              trackingDescription="Offer LE"
              enableDateCheckingForPriceDisplay
            />
          </ErrorBoundary>
        )}
        <ObservableWrapper>
          {!headlessMode && <BottomMobileRelatedTravelItems offer={offer} items={breadCrumbItems} title={travelDestinationsTitle} />}
        </ObservableWrapper>
        <ObservableWrapper ref={setFooterElement} />
      </OfferPageAssistantRailLayout>
    </OfferPageWrapper>
  </AnalyticsPageContext.Provider>
}

const mapStateToProps = (state: App.State, ownProps: OwnProps) => {
  const offerId = ownProps.propertyPageOfferId || ownProps.offerId || ''
  const offer: App.BedbankOffer | undefined = state.offer.bedbankOffers[offerId]

  return {
    regionCode: state.geo.currentRegionCode,
    currency: state.geo.currentCurrency,
    fetching: !!state.offer.offersLoading[offerId] && !offer,
    offer,
    traderInformation: state.offer.traderInformation[offerId]?.data,
    offerError: state.offer.offerErrors[offerId],
    headlessMode: state.system.headlessMode,
    relatedTravelItems: state.offer.relatedTravelItems,
  }
}

const ConnectedBedbankOfferPage = connect(mapStateToProps)(BedbankOfferPage)

function BedbankOfferPageRoute(props: Pick<OwnProps, 'propertyPageOfferId'>) {
  const { offerSlug, offerId } = useParams<'offerSlug' | 'offerId'>()
  const location = useLocation()

  return <ConnectedBedbankOfferPage
    offerId={offerId}
    offerSlug={offerSlug}
    locationHash={location.hash}
    propertyPageOfferId={props.propertyPageOfferId}
  />
}

export default BedbankOfferPageRoute
